import { useEffect, useState } from 'react';
import { PhoneCall, PhoneOff, Phone, Check, X, Clock } from 'lucide-react';
import { supabase } from '../supabase';

/**
 * Historique des tentatives de contact d'une commande, en feuille glissante.
 * Partagé entre admin, gestionnaire et marchand : il ne fait que lire.
 *
 * z-index au-dessus de la bottom nav (999) et de la sidebar (1002), sous les
 * dialogues de confirmation (2000) — même leçon que la feuille du motif.
 */

// 'call' = appel passé dont le résultat n'a pas (encore) été renseigné
const OUTCOME = {
  call:        { label: 'Appel passé',       Icon: Phone,     color: '#173b78', bg: '#f0f4ff', border: '#c7d7ef' },
  recall:      { label: 'Rappel demandé',    Icon: PhoneCall, color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  unreachable: { label: 'Injoignable',       Icon: PhoneOff,  color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  confirmed:   { label: 'Confirmée',         Icon: Check,     color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  cancelled:   { label: 'Annulée',           Icon: X,         color: '#c53a2d', bg: '#fef2f1', border: '#fecaca' },
};

/** "aujourd'hui 18:35", "hier 14:10", "09/09 17:10" */
export function formatAttemptTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const now  = new Date();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString())  return `aujourd'hui ${time}`;
  if (d.toDateString() === yest.toDateString()) return `hier ${time}`;
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${time}`;
}

/** "il y a 4 min", "il y a 3 h", "il y a 2 j" */
export function formatAttemptAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return '';
  const min = Math.floor(diff / 60000);
  if (min < 1)  return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

export default function ContactHistorySheet({ delivery, onClose }) {
  const [attempts, setAttempts] = useState(null); // null = chargement

  useEffect(() => {
    if (!delivery?.id) return;
    let cancelled = false;
    supabase
      .from('contact_attempts')
      .select('id, outcome, note, attempted_by, attempted_at')
      .eq('delivery_id', delivery.id)
      .order('attempted_at', { ascending: false })
      .then(({ data }) => { if (!cancelled) setAttempts(data || []); });
    return () => { cancelled = true; };
  }, [delivery?.id]);

  if (!delivery) return null;

  const count = attempts?.length ?? (Number(delivery.contact_attempts_count) || 0);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1500, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.6)', backdropFilter: 'blur(5px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '520px', margin: '0 auto', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -24px 70px rgba(0,0,0,0.28)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '14px 0 0', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e2e8f0' }} />
        </div>

        {/* En-tête */}
        <div style={{ padding: '14px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div className="ui-sheet-title">
              Historique des appels
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {delivery.client || 'Client'}
              {delivery.order_number ? ` · #${delivery.order_number}` : ''}
              {' · '}
              <strong style={{ color: '#173b78' }}>{count} appel{count > 1 ? 's' : ''}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ width: '32px', height: '32px', borderRadius: '9px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <X size={15} color="#94a3b8" />
          </button>
        </div>

        {/* Liste */}
        <div style={{ padding: '4px 16px 20px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {attempts === null ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[0, 1, 2].map((i) => <div key={i} style={{ height: '64px', borderRadius: '12px', background: '#f1f5f9', opacity: 0.7 - i * 0.15 }} />)}
            </div>
          ) : attempts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 12px', color: '#94a3b8', fontSize: '13px' }}>
              <Clock size={22} style={{ marginBottom: '8px' }} />
              <div>Aucun appel enregistré.</div>
              <div style={{ fontSize: '11px', marginTop: '4px' }}>L'historique commence à partir du prochain appel.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {attempts.map((a, i) => {
                const cfg = OUTCOME[a.outcome] || OUTCOME.call;
                const Icon = cfg.Icon;
                return (
                  <div key={a.id} style={{ display: 'flex', gap: '11px', padding: '12px 13px', borderRadius: '12px', background: i === 0 ? cfg.bg : 'white', border: `1.5px solid ${i === 0 ? cfg.border : '#eef0f6'}` }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: cfg.bg, border: `1px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={14} color={cfg.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: cfg.color }}>{cfg.label}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatAttemptTime(a.attempted_at)}</span>
                      </div>
                      {a.note && (
                        <div style={{ fontSize: '12px', color: '#334155', marginTop: '4px', lineHeight: 1.5 }}>{a.note}</div>
                      )}
                      {a.attempted_by && (
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '5px' }}>par {a.attempted_by}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
